import { useEffect, useMemo, useState } from 'react'
import { Page, TopBar } from '../components/Shell'
import { useStore } from '../state/store'
import { Skeleton } from '../components/Skeleton'
import { qaLog, SPEC_TITLES, reasonLabel, reasonSpec, type QaLogRow } from '../lib/qa'

/** The QA tally from Spec 18, kept by the app: error rate by lister, what went wrong by spec, every check. */
export function QaLog() {
  const { employees } = useStore()
  const [rows, setRows] = useState<QaLogRow[] | null>(null)
  const [days, setDays] = useState(7)
  const [err, setErr] = useState('')
  useEffect(() => { setRows(null); qaLog(days).then(setRows).catch((e) => { setErr((e as Error).message); setRows([]) }) }, [days])
  const name = (id: string | null) => employees.find((e) => e.employee_id === id)?.name ?? (id ? id : 'unknown lister')
  const list = rows ?? []
  const withError = list.filter((r) => r.result === 'sent_back' && r.fails.some((k) => !k.startsWith('ask:')))
  const rate = list.length ? Math.round(100 * withError.length / list.length) : 0
  const byLister = useMemo(() => {
    const m = new Map<string, { checked: number; back: number; specs: Map<string, number> }>()
    for (const r of list) {
      const k = r.listed_by ?? '----'
      const e = m.get(k) ?? { checked: 0, back: 0, specs: new Map() }
      e.checked++
      if (r.result === 'sent_back' && r.fails.length) { if (r.fails.some((k) => !k.startsWith('ask:'))) e.back++; for (const key of r.fails) { const s = reasonSpec(key, r.flags); e.specs.set(s, (e.specs.get(s) ?? 0) + 1) } }
      m.set(k, e)
    }
    return [...m.entries()].sort((a, b) => b[1].checked - a[1].checked)
  }, [list])
  const csv = () => {
    const esc = (v: unknown) => '"' + String(v ?? '').replace(/"/g, '""') + '"'
    const head = ['checked_at', 'result', 'sku', 'title', 'list_price', 'checked_by', 'listed_by', 'override_by', 'red', 'amber', 'fails', 'note', 'stamped']
    const lines = list.map((r) => [new Date(r.created_at).toLocaleString('en-US', { timeZone: 'America/New_York' }), r.result, r.sku, r.title, (r.list_cents / 100).toFixed(2), r.checked_by, r.listed_by ?? '', r.override_by ?? '', r.red_count, r.amber_count, r.fails.map((k) => reasonLabel(k, r.flags)).join('; '), r.note, r.stamped].map(esc).join(','))
    const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv' })
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `starchive-qa-${new Date().toISOString().slice(0, 10)}.csv`; a.click()
  }
  return (
    <Page bar={<TopBar back="QA" backTo="/qa" title="QA log" caption={`Last ${days} days`} />}>
      <div style={{ display: 'flex', gap: 8, padding: '4px 0 12px' }}>
        {[7, 30, 90].map((d) => <button key={d} type="button" className="seg__btn" aria-pressed={days === d} onClick={() => setDays(d)}>{d} days</button>)}
        <button type="button" className="seg__btn" style={{ marginLeft: 'auto' }} onClick={csv} disabled={!list.length}>CSV for the huddle</button>
      </div>
      <div className="qa-stats qa-stats--4">
        <div className="qa-stat"><span>Checked</span><b>{list.length}</b></div>
        <div className="qa-stat qa-stat--ok"><span>Passed</span><b>{list.filter((r) => r.result === 'pass').length}</b></div>
        <div className="qa-stat qa-stat--bad"><span>Sent back</span><b>{list.length - list.filter((r) => r.result === 'pass').length}</b></div>
        <div className={'qa-stat ' + (rate < 5 ? 'qa-stat--ok' : 'qa-stat--bad')}><span>Error rate · target under 5%</span><b>{rate}%</b></div>
      </div>
      <div className="hint" style={{ padding: '18px 6px 6px', textTransform: 'uppercase', letterSpacing: '.02em' }}>By lister</div>
      <div className="card">
        {rows === null && !err && <Skeleton rows={3} h={56} card={false} />}
        {err && <div className="empty" style={{ color: '#a4000f' }}>{err}</div>}
        {rows && byLister.length === 0 && !err && <div className="empty">No checks yet.</div>}
        {byLister.map(([id, e]) => {
          const pct = e.checked ? Math.round(100 * e.back / e.checked) : 0
          const specs = [...e.specs.entries()].sort((a, b) => b[1] - a[1]).map(([s, n]) => `${SPEC_TITLES[s]?.split(' · ')[1] ?? 'spec ' + s} (${n})`).join(', ')
          return (
            <div key={id} className="person" style={{ cursor: 'default' }}>
              <span className="person__initial">{name(id === '----' ? null : id).charAt(0)}</span>
              <span style={{ flexGrow: 1 }}><span className="person__name">{name(id === '----' ? null : id)} · {id}</span><span className="person__sub">{e.checked} checked · {e.back} with an error{specs ? ` · most common: ${specs}` : ''}</span></span>
              <span style={{ fontWeight: 700, fontSize: 17, color: pct === 0 ? '#1e6b33' : pct < 5 ? '#7a4a00' : '#a4000f' }}>{pct}%</span>
            </div>
          )
        })}
      </div>
      <div className="hint" style={{ padding: '18px 6px 6px', textTransform: 'uppercase', letterSpacing: '.02em' }}>Every check · newest first</div>
      <div className="card">
        {list.map((r) => (
          <div key={r.id} className="person" style={{ cursor: 'default' }}>
            <span style={{ flexGrow: 1, minWidth: 0 }}>
              <span className="person__name" style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</span>
              <span className="person__sub">{new Date(r.created_at).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {r.sku} · listed by {name(r.listed_by)} · checked by {name(r.checked_by)}{r.override_by ? ` · override by ${name(r.override_by)}` : ''}{r.fails.length ? ` · ${r.fails.map((k) => reasonLabel(k, r.flags)).join(', ')}` : ''}{r.note ? ` · "${r.note}"` : ''}{!r.stamped ? ' · not stamped' : ''}</span>
            </span>
            <span className={'qa-badge ' + (r.result === 'pass' ? 'qa-badge--ok' : 'qa-badge--bad')}>{r.result === 'pass' ? (r.override_by ? 'Pass · override' : 'Pass') : 'Sent back'}</span>
          </div>
        ))}
        {rows && list.length === 0 && !err && <div className="empty">Nothing in this window.</div>}
      </div>
      <div className="hint">This is the QA tally from Spec 18, kept by the app instead of on paper. Error rate is checks sent back with at least one flag, over all checks. A send-back only for repricing or a new tag is a request to the owner, not an error.</div>
    </Page>
  )
}
